import { NgModule } from '@angular/core';
import { RouterModule, Routes, Router } from '@angular/router';
import { ClientDetailsComponent } from './sommelier-clients/client-details/client-details.component';
import { AssistedCartComponent } from './sommelier-clients/assisted-cart/assisted-cart.component';
import { AssistedOrderDetailsComponent } from './sommelier-clients/assisted-order-details/assisted-order-details.component';
import { WineriesComponent } from './wineries/wineries.component';
import { WineryFormComponent } from './wineries/create-edit-winery/winery-form.component';
import { SommeliersComponent } from './sommeliers/sommeliers.component';
import { SommelierFormComponent } from './sommeliers/create-edit-sommelier/sommelier-form.component';
import { WinesComponent } from './wines/wines.component';
import { WineFormComponent } from './wines/create-edit-wine/wine-form.component';
import { CouponsComponent } from './coupons/coupons.component';
import { CouponFormComponent } from './coupons/create-edit-coupon/coupon-form.component';
import { CommunicationsComponent } from './communications/communications.component';
import { CommunicationFormComponent } from './communications/create-edit-communication/communication-form.component';
import { authGuard } from '@abp/ng.core';
import { CatalogComponent } from './catalog/catalog.component';
import { slugMatcher } from './shared/slug.matcher';
import { slugCanMatch } from './shared/guards/slug-can-match.guard';
import { routeRedirectCanActivate } from './shared/guards/route-redirect.guard';
import { adminGuard } from './shared/guards/admin.guard';
import { slugOwnershipGuard } from './shared/guards/slug-ownership.guard';
import { MetroProductDetailsComponent } from './shared/components/metro-product-details/metro-product-details.component';
import { OrdersComponent } from './orders/orders.component';
import { ProfileComponent } from './profile/profile.component';
import { ShoppingCartComponent } from './shopping-cart/shopping-cart.component';
import { MetroCheckoutComponent } from './shared/components/metro-checkout/metro-checkout.component';
import { BackofficeOrdersComponent } from './backoffice-orders/backoffice-orders.component';
import { MetroPaymentResultComponent } from './shared/components/metro-payment-result/metro-payment-result.component';
import { SommelierProfileComponent } from './sommelier-profile/sommelier-profile.component';
import { OrderDetailComponent } from './backoffice-orders/order-detail/order-detail.component';
import { CreateOrderComponent } from './backoffice-orders/create-order/create-order.component';
import { SettlementCommissionsComponent } from './reports/settlement-commissions.component';
import { PickupPointsComponent } from './pickuppoints/pickuppoints.component';
import { PickupFormComponent } from './pickuppoints/create-edit-pickuppoint/pickup-form.component';
import { OrderDetailsComponent } from './order/order-details.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { SommelierOrdersComponent } from './sommelier-orders/sommelier-orders.component';
import { ColppyHealthCheckComponent } from './colppy-healthcheck/colppy-healthcheck.component';
import { WineSalesReportComponent } from './reports/wine-sales-report/wine-sales-report.component';
import { ClientsReportComponent } from './reports/clients-report/clients-report.component';
import { MyNumbersComponent } from './my-numbers/my-numbers.component';
import { SommelierClientsComponent } from './sommelier-clients/sommelier-clients.component';
import { DeliveriesComponent } from './deliveries/deliveries.component';
import { MySommelierComponent } from './my-sommelier/my-sommelier.component';
import { AdminClientSommelierComponent } from './admin-client-sommelier/admin-client-sommelier.component';
import { EntityAction } from '@abp/ng.components/extensible';
import { GoogleCallbackComponent } from './auth/google-callback.component';

const routes: Routes = [
  {
    path: 'auth/google-callback',
    component: GoogleCallbackComponent,
  },
  {
    // winepassport-api#60 — URL fija de retorno del Web-Checkout de Allaria+, configurada una
    // vez en app.allariamas.dev. No lleva :slug (Allaria no admite pasar datos por request en
    // la URL de retorno) — SelectedSommelierService ya persiste el slug en localStorage.
    path: 'checkout/allaria-return',
    loadComponent: () => import('./allaria-return/allaria-return.component').then(m => m.AllariaReturnComponent),
    canActivate: [authGuard],
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [routeRedirectCanActivate],
    loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
  },

  {
    path: 'account',
    loadChildren: () => import('@abp/ng.account').then(m => m.AccountModule.forLazy()),
  },
  {
    path: 'account/confirm-email',
    component: ConfirmEmailComponent
  },
  {
    path: 'identity',
    loadChildren: () =>
      import('@abp/ng.identity').then(m =>
        m.IdentityModule.forLazy({
          // winepassport#339 / CU-44 — acción "Reasignar sommelier" en el menú "Opciones"
          // de la grilla de usuarios de ABP Identity.
          entityActionContributors: {
            'Identity.UsersComponent': [
              (actionList: any) => {
                const reassign = EntityAction.create({
                  text: '::ReassignSommelier',
                  action: (data: any) =>
                    data.getInjected(Router).navigate(['/backoffice/client-sommelier', data.record.id]),
                });
                // Insertar antes de "Borrar" (que siempre va último); si por lo que sea
                // no está, va al final.
                try {
                  actionList.addBefore(
                    reassign,
                    { text: 'AbpIdentity::Delete' },
                    (a: any, b: any) => a?.text === b?.text,
                  );
                } catch {
                  actionList.addTail(reassign);
                }
              },
            ],
          },
        }),
      ),
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'setting-management',
    loadChildren: () =>
      import('@abp/ng.setting-management').then(m => m.SettingManagementModule.forLazy()),
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wineries',
    component: WineriesComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wineries/create',
    component: WineryFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'wineries/edit/:id',
    component: WineryFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'coupons',
    component: CouponsComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'coupons/create',
    component: CouponFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'coupons/edit/:id',
    component: CouponFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'coupons/view/:id',
    component: CouponFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'communications',
    component: CommunicationsComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'communications/create',
    component: CommunicationFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'communications/edit/:id',
    component: CommunicationFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'sommeliers',
    component: SommeliersComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'sommeliers/create',
    component: SommelierFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'sommeliers/edit/:id',
    component: SommelierFormComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wines',
    component: WinesComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wines/create',
    component: WineFormComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wines/edit/:id',
    component: WineFormComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'settlement-commissions',
    component: SettlementCommissionsComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'wines-sales',
    component: WineSalesReportComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'clients-report',
    component: ClientsReportComponent,
    canActivate: [authGuard, adminGuard]

  },
  {
    path: 'colppy-healthcheck',
    component: ColppyHealthCheckComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/client-sommelier/:userId',
    component: AdminClientSommelierComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    matcher: slugMatcher,
    component: SommelierProfileComponent,
    canMatch: [slugCanMatch],
    data: { title: 'Wine Passport' }
  },
  {
    path: ':slug',
    component: SommelierProfileComponent,
    data: { title: 'Wine Passport' }
  },
  {
    path: 'backoffice/orders',
    component: BackofficeOrdersComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/orders/create',
    component: CreateOrderComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/orders/:orderId',
    component: OrderDetailComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/pickuppoints',
    component: PickupPointsComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/pickuppoints/create',
    component: PickupFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'backoffice/pickuppoints/edit/:id',
    component: PickupFormComponent,
    canActivate: [authGuard, adminGuard]
  },
  {
    path: 'colppy-healthcheck',
    component: ColppyHealthCheckComponent,
    canActivate: [authGuard, adminGuard]
  },

  {
    path: ':slug',
    children: [

      {
        path: 'catalog',
        component: CatalogComponent,
        data: { title: 'Wine Passport' }
      },
      {
        path: 'catalog/wine/:id',
        component: MetroProductDetailsComponent,
      },
      {
        path: 'orders',
        component: OrdersComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'sommelier-orders',
        component: SommelierOrdersComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'deliveries',
        component: DeliveriesComponent,
        canActivate: [authGuard]
      },
      {
        path: 'my-sommelier',
        component: MySommelierComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'my-numbers',
        component: MyNumbersComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'sommelier-clients',
        children: [
          {
            path: '',
            component: SommelierClientsComponent,
          },
          {
            path: ':userId',
            component: ClientDetailsComponent,
          },
          {
            path: ':userId/cart',
            component: AssistedCartComponent,
          },
          {
            path: ':userId/order/:orderId',
            component: AssistedOrderDetailsComponent,
          }
        ],
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'order-checkout',
        component: MetroCheckoutComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'shopping-cart',
        component: ShoppingCartComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
      {
        path: 'payment-status',
        component: MetroPaymentResultComponent,
        canActivate: [authGuard, slugOwnershipGuard]

      },
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [authGuard, slugOwnershipGuard]

      },
      {
        path: 'order/:id',
        component: OrderDetailsComponent,
        canActivate: [authGuard, slugOwnershipGuard]
      },
    ]
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },

];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule { }
